import React from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * Shared button primitives for the chat workspace panels (Sources / Chat /
 * Artifact). Every panel used to invent its own icon-button and pill-button
 * styling inline, with drifting padding/radius/gap — these two components
 * are the single source of truth so buttons look the same everywhere.
 *
 * Flat black & white — no shadows/gradients/color tints. Active state inverts
 * (solid black on white / solid white on black), matching the rest of the
 * workspace's monochrome look.
 */

export const tint = (isDarkMode) => ({
  fg: isDarkMode ? '#fff' : '#000',
  bg: isDarkMode ? '#000' : '#fff',
  border: isDarkMode ? '#3a3a3a' : '#000',
  hover: isDarkMode ? '#1c1c1c' : '#f0f0f0',
  text: isDarkMode ? '#eee' : '#111',
  secondary: isDarkMode ? '#999' : '#555',
});

/** Fixed-size icon-only button — 28x28 hit target, consistent radius/hover. */
export const IconButton = React.forwardRef(({
  children, isDarkMode, active = false, size = 28, title, onClick, disabled,
  style, ...rest
}, ref) => {
  const c = tint(isDarkMode);
  const [hovered, setHovered] = React.useState(false);
  return (
    <button
      ref={ref}
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        width: `${size}px`, height: `${size}px`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: 'none', borderRadius: '5px',
        background: active ? c.fg : hovered ? c.hover : 'transparent',
        color: active ? c.bg : c.secondary,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background-color 0.1s ease, color 0.1s ease',
        flexShrink: 0, padding: 0,
        ...style,
      }}
      {...rest}
    >
      {children}
    </button>
  );
});

/** Bordered pill button — for selects/toggles that show a text label (Scripts, Font, Language, Model…). */
export const PillButton = React.forwardRef(({
  children, isDarkMode, active = false, icon: Icon, chevron = false, title, onClick, disabled,
  style, ...rest
}, ref) => {
  const c = tint(isDarkMode);
  const [hovered, setHovered] = React.useState(false);
  return (
    <button
      ref={ref}
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        height: '28px', padding: '0 10px',
        display: 'flex', alignItems: 'center', gap: '5px',
        border: `1px solid ${c.border}`, borderRadius: '5px',
        background: active ? c.fg : hovered ? c.hover : 'transparent',
        color: active ? c.bg : c.text, fontSize: '11px', fontWeight: 600,
        whiteSpace: 'nowrap', cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background-color 0.1s ease, color 0.1s ease',
        flexShrink: 0, fontFamily: 'inherit',
        ...style,
      }}
      {...rest}
    >
      {Icon && <Icon size={12} />}
      {children}
      {chevron && <ChevronDown size={10} />}
    </button>
  );
});
